import { useState } from 'react';
import type { ImageAsset } from '@/assets';
import type { PoseId } from '@/content/articulation';
import { strings } from '@/content/strings';
import { MouthCutaway } from './MouthCutaway';
import { TongueSidesDiagram } from './TongueSidesDiagram';
import styles from './MouthHintCard.module.css';

export interface MouthDiagram {
  image: ImageAsset;
  /** What the picture shows, for screen readers. */
  alt: string;
  /** What to look at, in a few words ("Middle of the tongue up high, back low"). */
  look: string;
  /** The tongue pose, when the animated side view can show it (MouthCutaway). */
  pose?: PoseId;
  /** The roof of the mouth with the tongue's sides drawn on it (TongueSidesDiagram). */
  tongueSides?: boolean;
}

interface MouthPictureProps {
  /** The mouth position to aim for (marked with a tick). */
  shows: MouthDiagram;
  /** The position the attempt sounded like (a small crossed-out inset), if it has a picture. */
  insteadOf?: MouthDiagram | undefined;
}

/**
 * The placement cue's picture, drawn inside the word card in place of the word's picture (see
 * WordCueCard `coach`): the target mouth position, animated, with a tick; for a w or an l error,
 * that position as a small crossed-out inset in the corner. Tapping it plays the movement again.
 */
export function MouthPicture({ shows, insteadOf }: MouthPictureProps) {
  const [plays, setPlays] = useState(0);
  return (
    <button
      type="button"
      className={styles.picture}
      aria-label={`${shows.alt}. ${strings.hint.playAgain}`}
      onClick={() => {
        setPlays((n) => n + 1);
      }}
    >
      <span key={plays} className={styles.art}>
        <Art diagram={shows} still={false} />
      </span>
      <Mark kind="tick" />
      {insteadOf && (
        <span className={styles.inset} aria-label={`${strings.hint.insteadOf}: ${insteadOf.alt}`}>
          <span className={styles.art}>
            <Art diagram={insteadOf} still />
          </span>
          <Mark kind="cross" />
        </span>
      )}
    </button>
  );
}

function Art({ diagram, still }: { diagram: MouthDiagram; still: boolean }) {
  if (diagram.tongueSides) return <TongueSidesDiagram label={diagram.alt} />;
  if (diagram.pose) return <MouthCutaway pose={diagram.pose} still={still} label={diagram.alt} />;
  return (
    <img
      src={diagram.image.url}
      width={diagram.image.width}
      height={diagram.image.height}
      alt=""
      draggable={false}
    />
  );
}

function Mark({ kind }: { kind: 'tick' | 'cross' }) {
  return (
    <svg className={styles[kind]} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="11" />
      {kind === 'tick' ? (
        <path d="M6.5 12.5l3.5 3.5 7.5-8" />
      ) : (
        <path d="M7.5 7.5l9 9M16.5 7.5l-9 9" />
      )}
    </svg>
  );
}
